// LeaderboardCreatePage — thin wrapper around <LeaderboardConfigForm mode="create">.
//
// Responsibilities:
//   - Page chrome (PageHeader with breadcrumb back to the list + title).
//   - Wires the createLeaderboard mutation + optional reward batch creation.
//   - On success: invalidates the leaderboards list cache and navigates to the
//     newly-created leaderboard's detail page (or ?tab=rewards if any reward
//     failed so the user can finish setup).
//   - On cancel: navigates back to the list.
//   - On mutation error (leaderboard itself): surfaces a dismissable Alert
//     above the form so the user can correct the input and retry without
//     losing form state.
//   - On partial reward failure: shows a danger Alert listing the failed
//     reward names and navigates to the detail Rewards tab so the user can
//     retry the failed rows there.
//
// Mockup: docs/mockups/leaderboard-create.html. The 5-card form layout is
// owned by LeaderboardConfigForm; this page only contributes the header and
// the surrounding padding.

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Alert } from "../../components/Alert/Alert";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { ApiError, describeError } from "../../lib/errors";
import { getProject } from "../projects/api";
import { LeaderboardConfigForm } from "./LeaderboardConfigForm";
import { createLeaderboard, createReward } from "./api";
import type {
  CreateLeaderboardInput,
  CreateRewardInput,
  Leaderboard,
} from "./types";
import styles from "./LeaderboardCreatePage.module.css";

interface SubmitArgs {
  input: CreateLeaderboardInput;
  rewards: CreateRewardInput[];
}

interface SubmitResult {
  lb: Leaderboard;
  /** Names of rewards whose POST failed. Empty when everything succeeded. */
  failed: { name: string; reason: string }[];
}

export function LeaderboardCreatePage() {
  const { id: gameId = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Names of rewards that failed after the leaderboard itself was created.
  // Kept across the navigate() to the detail page is unnecessary — we show
  // the banner here briefly while staying on the form, then navigate.
  const [partialFail, setPartialFail] = useState<SubmitResult["failed"]>([]);

  const mutation = useMutation<SubmitResult, ApiError, SubmitArgs>({
    mutationFn: async ({ input, rewards }) => {
      const lb = await createLeaderboard(gameId, input);
      const failed: SubmitResult["failed"] = [];
      for (const r of rewards) {
        try {
          await createReward(gameId, lb.id, r);
        } catch (err) {
          const code =
            err instanceof ApiError ? err.code : "internal";
          failed.push({ name: r.name, reason: describeError(code) });
        }
      }
      return { lb, failed };
    },
    onSuccess: ({ lb, failed }) => {
      // Prefix-key invalidation: the list page keys queries by filter
      // (["leaderboards", gameId, filter]), so invalidating the
      // ["leaderboards", gameId] prefix covers every status/window combo.
      qc.invalidateQueries({ queryKey: ["leaderboards", gameId] });
      if (failed.length > 0) {
        // Surface the partial failure briefly and route to the Rewards tab so
        // the user can finish the broken rows manually.
        setPartialFail(failed);
        navigate(
          `/projects/${gameId}/leaderboards/${lb.id}?tab=rewards`,
        );
        return;
      }
      navigate(`/projects/${gameId}/leaderboards/${lb.id}`);
    },
  });

  // Project name for the breadcrumb (same pattern as LeaderboardsListPage).
  // Falls back to "Project" while the request is in flight or if it fails;
  // the unhandled MSW request in tests is bypassed and the fallback shows.
  const { data: project } = useQuery({
    queryKey: ["project", gameId],
    queryFn: () => getProject(gameId).then((r) => r.data),
    enabled: !!gameId,
  });

  const errorMessage = mutation.isError
    ? describeError(mutation.error?.code ?? "internal")
    : null;

  return (
    <>
      <PageHeader
        breadcrumb={[
          { label: "All projects", to: "/projects" },
          {
            label: project?.name ?? "Project",
            to: `/projects/${gameId}`,
          },
          { label: "Leaderboards", to: `/projects/${gameId}/leaderboards` },
        ]}
        title="New leaderboard"
      />
      <div className={styles.page}>
        {errorMessage && (
          <div className={styles.errorAlert}>
            <Alert variant="danger" title="Couldn't create leaderboard">
              {errorMessage}
            </Alert>
          </div>
        )}
        {partialFail.length > 0 && (
          <div className={styles.errorAlert}>
            <Alert
              variant="danger"
              title={`Leaderboard created, but ${partialFail.length} reward${
                partialFail.length === 1 ? "" : "s"
              } failed`}
            >
              <ul className={styles.partialFailList}>
                {partialFail.map((f, i) => (
                  <li key={i}>
                    <code>{f.name}</code> — {f.reason}
                  </li>
                ))}
              </ul>
              You can retry these on the Rewards tab.
            </Alert>
          </div>
        )}
        <LeaderboardConfigForm
          mode="create"
          saving={mutation.isPending}
          onSubmit={(input, rewards) => mutation.mutate({ input, rewards })}
          onCancel={() => navigate(`/projects/${gameId}/leaderboards`)}
        />
      </div>
    </>
  );
}
